/* "When?" — asked once, answered the same way everywhere.
 *
 * Six places in this app need to ask the same three questions: which day, what
 * time, how long. A task being put on a day, a block being moved, a reward
 * being claimed onto an afternoon, an hour of guitar, something typed straight
 * onto a month cell, a meeting you are adding by hand. Six dialogs would be
 * six slightly different answers about what happens when the server says no.
 *
 * The one piece of real design here is what happens on a refusal. The server
 * does not answer "invalid": it answers *"Overlaps “Design review” — a fixed
 * meeting with other people"*, and that sentence is shown in the dialog with
 * the form still filled in, so the fix is to change the time rather than to
 * start again. A refusal that closes the dialog and clears the fields is a
 * refusal that costs you the thing you had already typed.
 */

import Icon from './Icon';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import Dialog from './Dialog';
import { hhmm, toMinuteOfDay, human, friendlyDate } from './time';

export interface When { date: string; startMin: number; minutes: number }

/** Quick lengths, so the common case is one tap rather than typing a number. */
const LENGTHS = [15, 30, 45, 60, 90, 120];

export function ScheduleDialog({
  title, description, what, initial, confirmLabel = 'Put it on the day',
  extra, onClose, onSubmit
}: {
  title: string;
  description?: ReactNode;
  /** What is being placed, shown so the dialog says what it is about. */
  what?: string;
  initial: When;
  confirmLabel?: string;
  /** Anything the caller needs to ask on top of when — a title, say. */
  extra?: ReactNode;
  onClose: () => void;
  onSubmit: (when: When) => Promise<unknown>;
}) {
  const [date, setDate] = useState(initial.date);
  const [start, setStart] = useState(hhmm(initial.startMin));
  const [minutes, setMinutes] = useState(String(initial.minutes));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => onSubmit({
      date,
      startMin: toMinuteOfDay(start),
      minutes: Math.max(5, Math.min(1440, Number(minutes) || initial.minutes))
    }),
    onSuccess: () => onClose(),
    /* The sentence stays, the form stays filled. */
    onError: (e: Error) => setError(e.message)
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  const startMin = toMinuteOfDay(start);
  const length = Math.max(5, Math.min(1440, Number(minutes) || initial.minutes));
  const past = startMin + length > 1440;

  return (
    <Dialog title={title} icon="clock" description={description} onClose={onClose}>
      <form className="when-form" onSubmit={submit}>
        {what && <p className="when-what"><Icon name="tasks" size={15} /><strong>{what}</strong></p>}
        {extra}

        <div className="field">
          <label htmlFor="when-date">Which day</label>
          <input id="when-date" type="date" value={date}
                 onChange={e => setDate(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="when-start">Starts at</label>
          <input id="when-start" type="time" value={start}
                 onChange={e => setStart(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="when-mins">Minutes</label>
          <input id="when-mins" type="number" min="5" max="1440" step="5" value={minutes}
                 onChange={e => setMinutes(e.target.value)} />
        </div>

        <div className="field-wide inline wrap" role="group" aria-label="Common lengths">
          {LENGTHS.map(m => (
            <button key={m} type="button"
                    className={`pill${length === m ? ' is-active' : ''}`}
                    aria-pressed={length === m}
                    onClick={() => setMinutes(String(m))}>{human(m)}</button>
          ))}
        </div>

        {/* Said before it is submitted, because the arithmetic is the whole
            question: you are choosing when you are free again. */}
        <p className="muted small field-wide">
          {friendlyDate(date)}, {hhmm(startMin)}–{hhmm(Math.min(1439, startMin + length))}.
          {past && ' That runs past the end of the day.'}
        </p>

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit"
                  disabled={save.isPending || past}>
            {save.isPending ? 'Saving…' : confirmLabel}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}

/** A yes/no that has consequences worth spelling out. Used where `confirm()`
    was — the browser's own dialog cannot say what else will happen, and cannot
    be styled, focused or read back the way this can. */
export function ConfirmDialog({ title, description, confirmLabel, danger, onClose, onConfirm }: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<unknown> | void;
}) {
  const [error, setError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async () => onConfirm(),
    onSuccess: () => onClose(),
    onError: (e: Error) => setError(e.message)
  });

  return (
    <Dialog title={title} icon={danger ? 'trash' : 'help'} onClose={onClose}>
      <div className="when-form">
        <p className="field-wide">{description}</p>
        {error && <p className="auth-err field-wide" role="alert">{error}</p>}
        <div className="inline field-wide">
          <button className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`}
                  disabled={run.isPending}
                  onClick={() => run.mutate()}>
            {run.isPending ? 'Working…' : confirmLabel}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Dialog>
  );
}
